import React, { useContext, useEffect, useRef, useState } from "react";
import "../../../../constants/report.css";
import axios from "axios";
import { AuthContext } from "../../../../contexts/AuthContext";
import { tempUrl } from "../../../../contexts/ContextProvider";
import { Loader } from "../../../../components";
import { formatDate } from "../../../../constants/helper";
import {
  Autocomplete,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  FormControlLabel,
  TextField,
} from "@mui/material";
import { Col, Container, Form, Row } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { downloadExcel } from "react-export-table-to-excel";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import { useReactToPrint } from "react-to-print";
import useScreenWidth from "../../../../hooks/useScreenWidth";

const toSqlDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const TarikKonsumen = () => {
  const { user, setting } = useContext(AuthContext);
  const screenSize = useScreenWidth();
  const reportTemplateRef = useRef(null);
  const tableRef = useRef(null);
  const nowDate = new Date();

  const [semua, setSemua] = useState(false);
  const [dariTanggal, setDariTanggal] = useState(
    new Date(user.tutupperiode.dariTanggal),
  );
  const [sampaiTanggal, setSampaiTanggal] = useState(
    new Date(user.tutupperiode.sampaiTanggal),
  );
  const [kodeCabang, setKodeCabang] = useState("");
  const [namaCabang, setNamaCabang] = useState("");
  const [cabangs, setCabangs] = useState([]);
  const [konsumens, setKonsumens] = useState([]);
  const [preview, setPreview] = useState(false);
  const [loading, setLoading] = useState(false);

  const cabangOptions = cabangs.map((cabang) => ({
    label: `${cabang.id} - ${cabang.namaCabang}`,
    id: cabang.id,
  }));

  useEffect(() => {
    const getCabangs = async () => {
      const response = await axios.post(`${tempUrl}/cabangs`, {
        _id: user.id,
        token: user.token,
        kodeCabang: user.cabang.id,
      });
      setCabangs(response.data);
    };

    getCabangs();
    if (user.tipeUser !== "OWNER/DIREKSI") {
      setKodeCabang(user.cabang.id);
      setNamaCabang(`${user.cabang.id} - ${user.cabang.namaCabang}`);
    }
  }, [user]);

  const tampilLaporan = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${tempUrl}/tarikKonsumen`, {
        semua,
        dariTanggal: toSqlDate(dariTanggal),
        sampaiTanggal: toSqlDate(sampaiTanggal),
        kodeCabang,
        _id: user.id,
        token: user.token,
      });
      setKonsumens(response.data);
      setPreview(true);
    } catch (error) {
      alert(error.response?.data?.message || "Data konsumen gagal ditampilkan.");
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = useReactToPrint({
    content: () => reportTemplateRef.current,
    documentTitle: "TarikKonsumen",
  });

  const downloadTarikKonsumenExcel = () => {
    if (!tableRef.current) {
      return;
    }

    downloadExcel(
      {
        fileName: "TarikKonsumen.xls",
        sheet: "TarikKonsumen",
      },
      tableRef.current,
    );
  };

  if (loading) {
    return <Loader persenLoading="50" />;
  }

  return (
    <Container>
      <h3>Laporan</h3>
      <h5 style={{ fontWeight: 400 }}>Tarik Konsumen</h5>
      <hr />

      <Box sx={{ mt: 4 }}>
        <Form onSubmit={tampilLaporan}>
          <Row>
            <Col sm={6}>
              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm="4" style={textRight(screenSize)}>
                  Cabang :
                </Form.Label>
                <Col sm="8">
                  {user.tipeUser === "OWNER/DIREKSI" ? (
                    <Autocomplete
                      size="small"
                      disablePortal
                      options={cabangOptions}
                      renderInput={(params) => (
                        <TextField
                          size="small"
                          placeholder="SEMUA CABANG"
                          {...params}
                        />
                      )}
                      onChange={(event, value) => {
                        setKodeCabang(value?.id || "");
                        setNamaCabang(value?.label || "");
                        setPreview(false);
                      }}
                      value={
                        kodeCabang
                          ? { id: kodeCabang, label: namaCabang }
                          : null
                      }
                    />
                  ) : (
                    <Form.Control value={namaCabang} disabled readOnly />
                  )}
                </Col>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col sm={6}>
              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm="4" style={textRight(screenSize)}>
                  Semua :
                </Form.Label>
                <Col sm="8">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={semua}
                        onChange={(event) => {
                          setSemua(event.target.checked);
                          setPreview(false);
                        }}
                      />
                    }
                    label="Tampilkan semua tanggal"
                  />
                </Col>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col sm={6}>
              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm="4" style={textRight(screenSize)}>
                  Dari :
                </Form.Label>
                <Col sm="8">
                  <DatePicker
                    required={!semua}
                    disabled={semua}
                    dateFormat="dd/MM/yyyy"
                    selected={dariTanggal}
                    customInput={<Form.Control required={!semua} />}
                    onChange={(date) => {
                      setDariTanggal(date);
                      setPreview(false);
                    }}
                  />
                </Col>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col sm={6}>
              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm="4" style={textRight(screenSize)}>
                  Sampai :
                </Form.Label>
                <Col sm="8">
                  <DatePicker
                    required={!semua}
                    disabled={semua}
                    dateFormat="dd/MM/yyyy"
                    selected={sampaiTanggal}
                    minDate={dariTanggal}
                    customInput={<Form.Control required={!semua} />}
                    onChange={(date) => {
                      setSampaiTanggal(date);
                      setPreview(false);
                    }}
                  />
                </Col>
              </Form.Group>
            </Col>
          </Row>

          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            type="submit"
            style={{ marginTop: "20px" }}
          >
            LIHAT
          </Button>
        </Form>
      </Box>

      {preview && (
        <div style={{ marginTop: "10px" }}>
          <ButtonGroup variant="outlined" color="secondary">
            <Button startIcon={<PrintIcon />} onClick={generatePDF}>
              PDF
            </Button>
            <Button
              startIcon={<SearchIcon />}
              onClick={downloadTarikKonsumenExcel}
            >
              Excel
            </Button>
          </ButtonGroup>
        </div>
      )}

      <div ref={reportTemplateRef} style={pdfContainer}>
        <div style={{ visibility: preview ? "visible" : "hidden" }}>
          <p>
            {setting.namaPerusahaan} - {setting.alamatPerusahaan}
          </p>
          <p>({setting.kabupatenPerusahaan})</p>
          <p>{setting.kotaPerusahaan}</p>
          <p>NO. TELP. {setting.teleponPerusahaan}</p>
          <p>
            Dicetak Oleh: {user.username} | Tanggal: {formatDate(nowDate)} | Jam:{" "}
            {nowDate.toLocaleTimeString("id-ID")}
          </p>
          <h5 style={reportTitle}>Tarik Konsumen</h5>
          <p>
            Periode : {semua ? "SEMUA TANGGAL" : `${formatDate(dariTanggal)} s.d. ${formatDate(sampaiTanggal)}`}
          </p>
          <p>Cabang : {namaCabang || "SEMUA CABANG"}</p>

          <table className="styled-table" ref={tableRef} style={reportTable}>
            <thead>
              <tr>
                <th style={cellBold}>NAMA KONSUMEN</th>
                <th style={cellBold}>NO. HP</th>
                <th style={cellBold}>TANGGAL TERAKHIR SERVIS</th>
              </tr>
            </thead>
            <tbody>
              {konsumens.map((konsumen, index) => (
                <tr key={`${konsumen.namaCustomer}-${konsumen.noTeleponCustomer}-${index}`}>
                  <td style={cell}>{konsumen.namaCustomer || "-"}</td>
                  <td style={cell}>{konsumen.noTeleponCustomer || "-"}</td>
                  <td style={cell}>{formatDate(konsumen.tanggalTerakhirServis)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Container>
  );
};

const textRight = (screenSize) => ({
  textAlign: screenSize >= 650 ? "right" : "left",
});

const pdfContainer = {
  padding: "20px",
  letterSpacing: "0.01px",
  fontSize: "14px",
  fontFamily: "sans-serif",
};

const reportTitle = {
  textAlign: "center",
  fontWeight: 700,
  fontSize: "22px",
};

const reportTable = { fontSize: "14px" };
const cell = { border: "1px solid black" };
const cellBold = { ...cell, fontWeight: 700 };

export default TarikKonsumen;
